import { routeGuard } from "@ecca/vue-auth-plugin";
import { ref } from "vue";
import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { contentRoutesNames } from "@/modules/content/routes";
import { productRoutesNames } from "@/modules/products/routes";
import { reportsRoutesNames } from "@/modules/reports/routes";
import { rootRoutesNames } from "@/router/routes";
import { errorRoutesNames } from "@/shared/ui/views/errors/routes";
import { useAppStore } from "@/store/app";

const isBrowserNavigation = ref(false);
window.addEventListener("popstate", () => {
  isBrowserNavigation.value = true;
});

const handleNavigationNotification = (
  from: RouteLocationNormalized,
  step: `before` | `after`,
  to?: RouteLocationNormalized,
) => {
  const store = useAppStore();

  if (to?.name && step === `before`) {
    // check and remove notification bound to the route
    store.removeRestrictedNotifications(to.name as string);
  }

  if (isBrowserNavigation.value && from.name) {
    if (step === `before`) {
      // hide notifications on browser navigation because they block routing if open
      store.hideNotifications();
    }
    if (step === `after`) {
      // restore notifications after navigation is done and reset the flag
      store.showNotifications();
      isBrowserNavigation.value = false;
    }
  }
};

const isWorkInProgress = (to: RouteLocationNormalized) => {
  const store = useAppStore();

  if (store.editState.isWorkInProgress) {
    store.setCachedTargetRoute(to);
    store.setIsWorkInProgressModalOpen(true);
  }

  return store.editState.isWorkInProgress;
};

const clientIdGuard = async (
  to: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const store = useAppStore();

  //User potentially not logged in, allow routeGuard to handle the error
  if (!store.clientID) {
    return true;
  }
  const urlClientId = to.params.clientId as string;
  // if clientId is set check if user has access to it
  if (urlClientId && store.userInfo?.clients) {
    // if user has no access to the clientId redirect to forbidden error
    if (
      !store.userInfo.clients.some((client) => client.clientID === urlClientId)
    ) {
      console.error(`Client ID ${urlClientId} not found in user info.`);
      store.setForbiddenError(true, {
        level: "global",
        message: "You do not have permission to access this workspace.",
      });
      next();
      return false;
    }

    // change clientID in store if user has access and it is different from current one
    if (!store?.clientID || store?.clientID !== urlClientId) {
      await store.setClientID(urlClientId);
      next(to); //  redirect to the same route but with updated clientId in store
      return false;
    }
  }

  //check if clientId is set in the route params if not add current clientId
  if (!to.params.clientId && to.name !== errorRoutesNames.notFound()) {
    to.params.clientId = store.clientID as string;
    next(to);
    return false;
  }

  // all good, continue with navigation
  return true;
};

const redirectToDefaultPage = (next: NavigationGuardNext) => {
  const store = useAppStore();

  if (store.auth("products")) {
    return next({
      name: productRoutesNames.list(),
      params: { clientId: store.clientID },
    });
  }

  if (store.auth("reporting")) {
    return next({
      name: reportsRoutesNames.list(),
      params: { clientId: store.clientID },
    });
  }

  if (
    store.auth("contents:skl") ||
    store.auth("contents:storyboards") ||
    store.auth("contents:rich") ||
    store.auth("contents:written_content")
  ) {
    return next({
      name: contentRoutesNames.lists.root,
      params: { clientId: store.clientID },
    });
  }

  return next(false);
};

export const beforeEachGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const store = useAppStore();
  store.setForbiddenError(false);

  if (to.meta.scope && typeof to.meta.scope !== "string") {
    console.error("Invalid scope type, expected string", to.meta.scope);
    store.setForbiddenError(true);
    next(false);
    return;
  }

  if (!(await clientIdGuard(to, next))) return;

  const routerGuardResult = await routeGuard(to, from, next, {
    skipNavigationOnValid: true,
  });

  if (!routerGuardResult) {
    store.setForbiddenError(true);
    return;
  }

  if (!store.clientID) {
    store.setForbiddenError(true, {
      level: "global",
      message: "Cannot access workspace. Please select a workspace.",
    });
    next(false);
    return;
  }

  handleNavigationNotification(from, `before`, to);

  if (store.isLoading) return next(false);

  if (isWorkInProgress(to)) return next(false);

  if (to.name === rootRoutesNames.home()) return redirectToDefaultPage(next);

  if (to.name === errorRoutesNames.notFound()) {
    store.setNotFoundError(true, {
      level: "global",
      message: "The page you are looking for does not exist.",
    });
    next();
    return;
  }

if (to.meta.scope && !store.auth(to.meta.scope as string)) {
    store.setForbiddenError(true);
    next();
    return;
  }

  next();
  return;
};

export const afterEachHook = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
) => {
  const store = useAppStore();

  if (to.name !== errorRoutesNames.notFound() && !store.forbidden) {
    store.resetErrors();
  }

  handleNavigationNotification(from, `after`, to);
};
